// src/lib/id-upload.js — N2-IA LOT 1 (ADR-189)
//
// Flux client d'upload sécurisé d'une pièce d'identité depuis le formulaire de
// bienvenue (PublicFormPage). Étapes :
//   1. compression canvas auto (images) : resize longest-edge 2000px + ré-encode
//      JPEG q0.8 (résout HEIC iPhone + fichiers > 10 Mo). PDF passe tel quel.
//   2. POST id-upload-url → signed upload URL Storage (path reconstruit serveur).
//   3. PUT du fichier sur la signed URL.
//   4. POST id-upload-confirm → preuve d'objet + magic-bytes côté serveur.
//
// Le path est TOUJOURS reconstruit serveur : le client n'envoie jamais de path.
// Mapping référence de champ formulaire → référence document DB (enum SQL).

const API = '/api/beds24-mapping';

// Mapping reference de champ (PublicFormPage) → ref document DB (CHECK SQL).
export const FIELD_REF_TO_DOC_REF = {
  passport_photo: 'passport',
  id_recto: 'cni_recto',
  id_verso: 'cni_verso',
  // Titre de séjour (décision 3, 2026-09-06) — recto + verso comme la CNI.
  // ⚠️ Refs acceptées serveur (REF_VALUES) MAIS le CHECK SQL id_documents.ref
  // doit être étendu par la migration 20261225000001 AVANT tout upload réel.
  ts_recto: 'ts_recto',
  ts_verso: 'ts_verso',
};

const MAX_EDGE = 2000; // longest edge px après resize
const JPEG_QUALITY = 0.8;
const PDF_MIME = 'application/pdf';

// Mimes acceptés en sortie (après compression éventuelle).
const ACCEPTED_OUT_MIME = new Set(['image/jpeg', 'image/png', PDF_MIME]);

/**
 * Compresse une image via canvas. Retourne un Blob JPEG (mime image/jpeg).
 * Les PDF ne passent PAS par ici (gérés en amont). Si le décodage échoue
 * (format non supporté, HEIC non décodable par le navigateur), rejette avec
 * un code 'decode_failed' pour message utilisateur dédié.
 *
 * @param {File} file
 * @returns {Promise<Blob>}
 */
export function compressImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        let { width, height } = img;
        const longest = Math.max(width, height);
        if (longest > MAX_EDGE) {
          const scale = MAX_EDGE / longest;
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          URL.revokeObjectURL(url);
          const err = new Error('decode_failed');
          err.code = 'decode_failed';
          return reject(err);
        }
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(url);
            if (!blob) {
              const err = new Error('decode_failed');
              err.code = 'decode_failed';
              return reject(err);
            }
            resolve(blob);
          },
          'image/jpeg',
          JPEG_QUALITY,
        );
      } catch (e) {
        URL.revokeObjectURL(url);
        const err = new Error('decode_failed');
        err.code = 'decode_failed';
        reject(err);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      const err = new Error('decode_failed');
      err.code = 'decode_failed';
      reject(err);
    };
    img.src = url;
  });
}

/**
 * Prépare le payload d'upload : compresse les images, laisse passer les PDF.
 * Retourne { blob, mime }. Lève err.code='unsupported_format' si ni image ni PDF.
 *
 * @param {File} file
 * @returns {Promise<{blob: Blob, mime: string}>}
 */
export async function prepareUpload(file) {
  const type = file.type || '';
  if (type === PDF_MIME) {
    return { blob: file, mime: PDF_MIME };
  }
  // Tout le reste (image/*, HEIC, etc.) passe par le canvas → JPEG.
  // Si le navigateur ne sait pas décoder (HEIC sur certains), compressImage rejette.
  if (type.startsWith('image/') || type === '' || /\.(heic|heif|jpe?g|png|webp)$/i.test(file.name || '')) {
    const blob = await compressImage(file);
    return { blob, mime: 'image/jpeg' };
  }
  const err = new Error('unsupported_format');
  err.code = 'unsupported_format';
  throw err;
}

/**
 * Flux complet d'upload d'une pièce d'identité.
 *
 * @param {object} args
 * @param {string} args.token — token unified du formulaire.
 * @param {string} args.fieldRef — référence de champ ('passport_photo'|'id_recto'|'id_verso').
 * @param {File} args.file — fichier sélectionné par l'utilisateur.
 * @returns {Promise<{ref: string}>} ref document confirmé.
 * @throws Error avec .code ('unsupported_format'|'url_failed'|'put_failed'|'confirm_failed'|...)
 */
export async function uploadIdDocument({ token, fieldRef, file, onPhase }) {
  const docRef = FIELD_REF_TO_DOC_REF[fieldRef];
  if (!docRef) {
    const err = new Error('unknown_ref');
    err.code = 'unknown_ref';
    throw err;
  }

  // 1. Compression / préparation.
  const { blob, mime } = await prepareUpload(file);
  if (!ACCEPTED_OUT_MIME.has(mime)) {
    const err = new Error('unsupported_format');
    err.code = 'unsupported_format';
    throw err;
  }

  // 2. Demande de signed upload URL.
  const urlRes = await fetch(`${API}?action=id-upload-url`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'id-upload-url', token, ref: docRef, mime }),
  });
  const urlBody = await urlRes.json().catch(() => ({}));
  // B1 (verrou 409-au-reload) — un 409 `already_resolved` portant le status
  // `verified` signifie : le document est DÉJÀ là et vérifié côté serveur.
  // C'est un SUCCÈS (ligne verte restaurée), plus jamais une erreur. Le fichier
  // fraîchement choisi n'est pas envoyé — pour le remplacer, le voyageur passe
  // par le ✕ (id-upload-remove, Q-CROIX-VERIFIED). Un 409 `processing` reste
  // une erreur (vérification en cours ailleurs).
  if (urlRes.status === 409 && urlBody.error === 'already_resolved' && urlBody.status === 'verified') {
    return { ref: docRef, alreadyResolved: true };
  }
  if (!urlRes.ok || !urlBody.signed_url) {
    const err = new Error(urlBody.error || 'url_failed');
    err.code = urlBody.error || 'url_failed';
    throw err;
  }

  // 3. PUT du fichier sur la signed URL.
  const putRes = await fetch(urlBody.signed_url, {
    method: 'PUT',
    headers: { 'Content-Type': mime },
    body: blob,
  });
  if (!putRes.ok) {
    const err = new Error('put_failed');
    err.code = 'put_failed';
    throw err;
  }

  // 4. Confirm serveur (preuve d'objet + magic-bytes + contrôle éventuel).
  // C4 — le confirm peut durer quelques secondes (analyse automatique côté
  // serveur) : onPhase('checking') permet au conteneur d'afficher le spinner
  // « Vérification de votre document… » distinct d'« Envoi en cours… ».
  // Paramètre STRICTEMENT optionnel : les appels historiques (V1) ne le
  // passent pas et gardent un comportement identique.
  if (typeof onPhase === 'function') {
    try { onPhase('checking'); } catch (_) { /* affichage best-effort */ }
  }
  const confRes = await fetch(`${API}?action=id-upload-confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'id-upload-confirm', token, ref: docRef, mime }),
  });
  const confBody = await confRes.json().catch(() => ({}));
  if (!confRes.ok || !confBody.ok) {
    const err = new Error(confBody.error || 'confirm_failed');
    err.code = confBody.error || 'confirm_failed';
    throw err;
  }

  // `controle` : ÉNUMS SEULS du contrôle automatique (null si non réalisé —
  // fail-open). Les appels V1 ignorent ce champ additif.
  return { ref: docRef, controle: confBody.controle || null };
}

/**
 * ✕ — retrait RÉEL d'un document (M3, Q-CROIX-VERIFIED) : DELETE de la ligne
 * id_documents + suppression Storage de toutes les extensions sœurs, côté
 * serveur (action anon id-upload-remove, token validé + ref whitelistée).
 *
 * @param {object} args
 * @param {string} args.token — token unified du formulaire.
 * @param {string} args.fieldRef — référence de champ formulaire.
 * @returns {Promise<{ref: string}>}
 */
export async function removeIdDocument({ token, fieldRef }) {
  const docRef = FIELD_REF_TO_DOC_REF[fieldRef];
  if (!docRef) {
    const err = new Error('unknown_ref');
    err.code = 'unknown_ref';
    throw err;
  }
  const res = await fetch(`${API}?action=id-upload-remove`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'id-upload-remove', token, ref: docRef }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || !body.ok) {
    const err = new Error(body.error || 'remove_failed');
    err.code = body.error || 'remove_failed';
    throw err;
  }
  return { ref: docRef };
}
